import {Component, OnInit} from '@angular/core';
import {RestApiService} from '../../../services/api/rest-api.service';
import {TabPanel, TabView} from 'primeng/tabview';
import {DialogService, DynamicDialogConfig, DynamicDialogRef} from 'primeng/dynamicdialog';
import {MediaSource, MediaSourceType} from '../../../models/media-source';
import {FileUpload} from 'primeng/fileupload';
import {UploadMediaRequest} from '../../../models/requests/upload-media-request';
import {readFileAsBase64} from '../../../utils/files-utils';
import {FilterMediaSourceRequest} from '../../../models/requests/filter-media-source.request';
import {NgIf} from '@angular/common';
import {MediaFormComponent} from '../media-form/media-form.component';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CheckboxComponent} from '../../form/checkbox/checkbox.component';
import {BehaviorSubject} from 'rxjs';
import {InputComponent} from '../../form/input/input.component';
import {Paginator, PaginatorState} from 'primeng/paginator';
import {BlockableDivComponent} from '../../form/blockable-div/blockable-div.component';
import {BlockUI} from 'primeng/blockui';
import {Slider} from 'primeng/slider';
import {FormsModule} from '@angular/forms';
import {Tooltip} from 'primeng/tooltip';
import {FString} from '../../../services/filters/string-filter';

@UntilDestroy()
@Component({
  selector: 'app-media-manager',
  imports: [
    TabView,
    TabPanel,
    FileUpload,
    CheckboxComponent,
    InputComponent,
    Paginator,
    BlockableDivComponent,
    BlockUI,
    Slider,
    FormsModule,
    NgIf,
    Tooltip,
  ],
  templateUrl: './media-manager.component.html',
  styleUrl: './media-manager.component.scss'
})
export class MediaManagerComponent implements OnInit {
  protected readonly MediaSourceType = MediaSourceType;

  filter: FilterMediaSourceRequest = {
    sortingKey: 'name',
    sortingDirectionAsc: true,
    from: 0,
    amount: 10,
    showDeleted: false,
  } as FilterMediaSourceRequest;
  mediaId: number;
  loading = false;
  dragging = false;

  selectedMediaSource?: MediaSource;
  accept: string;
  multipleUpload = false;
  maxFileSize?: number = undefined;
  category = 'default';
  uploadingFiles?: FileList;

  mediaSources: MediaSource[] = [];
  mediaSourcesCount = 0;
  paginationSize = 10;

  showDeleted$ = new BehaviorSubject<boolean>(false);
  genericText$ = new BehaviorSubject<string>('');
  iconSize: number = 150;

  get firstElementPagintor(): number {
    return this.filter.from! / this.filter.amount!;
  }

  constructor(private api: RestApiService,
              private dialogService: DialogService,
              private dynamicDialogRef: DynamicDialogRef, private config: DynamicDialogConfig) {
    this.mediaId = (config.data as Configuration).mediaId ?? 0;
    this.accept = (config.data as Configuration).accept ?? '*/*';
    this.multipleUpload = (config.data as Configuration).multipleUpload;
    this.maxFileSize = (config.data as Configuration).maxFileSize;
    this.category = (config.data as Configuration).category ?? 'default';
    this.uploadingFiles = (config.data as Configuration).uploadingFiles;
  }

  ngOnInit(): void {
    this.showDeleted$
      .pipe(untilDestroyed(this))
      .subscribe(deleted => {
          this.filter.showDeleted = deleted;
          this.loadData();
        }
      );
    this.genericText$
      .pipe(untilDestroyed(this))
      .subscribe(text => {
          this.filter.genericText = text;
          this.loadData();
        }
      );
    if (this.uploadingFiles) {
      for (let file of this.uploadingFiles) {
        this.uploadFile(file);
      }
    }
    this.loadData();
  }

  close() {
    this.dynamicDialogRef.close(this.selectedMediaSource);
  }

  async loadData() {
    try {
      this.loading = true;
      this.filter.category = new FString(this.category);
      const responseFilter = await this.api.mediaSources.filter(this.filter);
      const mediaSourceListedResponse = responseFilter.data;
      this.mediaSources = mediaSourceListedResponse.dataList;
      this.mediaSourcesCount = mediaSourceListedResponse.count;
      if (this.mediaId) {
        const response = await this.api.mediaSources.getById(this.mediaId);
        this.selectedMediaSource = response.data;
      }
    } finally {
      this.loading = false;

    }
  }


  async onUpload(event: any) {
    for (let file of event.files) {
      await this.uploadFile(file);
    }
  }

  async uploadFile(file: File) {
    const base64Content = await readFileAsBase64(file);
    const request = new UploadMediaRequest();
    request.filename = file.name;
    request.name = file.name;
    request.data = base64Content;
    request.category = this.category;
    request.description = file.name;
    request.mediaSourceType = MediaSourceType.Image;
    const reponse = await this.api.mediaSources.upload(request);
    this.selectedMediaSource = reponse.data;
    this.selectMedia(this.selectedMediaSource);
  }

  selectMedia(media: MediaSource) {
    this.selectedMediaSource = media;
    const dialog = MediaFormComponent.openDialog(this.dialogService, media);
    dialog.onClose
      .pipe(untilDestroyed(this))
      .subscribe((mediaSource: MediaSource | 'reload') => {
          if (mediaSource === 'reload') {
            this.loadData();
          } else if (mediaSource) {
            this.selectedMediaSource = mediaSource;
            this.close();
          }
        }
      );
  }


  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.dragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.dragging = false;
  }

  async onDrop(event: DragEvent) {
    event.preventDefault();
    this.dragging = false;

    if (!event.dataTransfer || event.dataTransfer?.files.length === 0) {
      return;
    }

    for (let file of event.dataTransfer!.files) {
      await this.uploadFile(file);
    }
    await this.loadData();
  }

  static openDialog(dialogService: DialogService,
                    data: {
                      mediaId?: number,
                      accept?: string,
                      multipleUpload?: boolean,
                      maxFileSize?: number,
                      category?: string,
                      uploadingFiles?: FileList
                    } = {
                      mediaId: 0,
                      accept: 'image/*',
                      multipleUpload: false,
                      maxFileSize: undefined,
                      category: 'default',
                      uploadingFiles: undefined,
                    }
  ): DynamicDialogRef {
    return dialogService.open(MediaManagerComponent, {
      width: '60%',
      height: '100vh',
      closable: true,
      closeOnEscape: true,
      header: 'Media manager',
      modal: true,
      showHeader: true,
      data: {
        mediaId: data?.mediaId ?? 0,
        accept: data?.accept ?? 'image/*',
        multipleUpload: data?.multipleUpload ?? false,
        maxFileSize: data?.maxFileSize,
        category: data?.category ?? 'default',
        uploadingFiles: data?.uploadingFiles,
      } as Configuration
    });
  }

  onPageChange($event: PaginatorState) {
    this.filter.from = $event.page! * this.filter.amount!;
    this.paginationSize = $event.rows!;
    this.filter.amount = this.paginationSize;
    this.loadData();
  }
}

interface Configuration {
  mediaId: number;
  accept: string;
  multipleUpload: boolean;
  maxFileSize?: number;
  category: string;
  uploadingFiles: FileList;
}
